import useRouter from "common/hooks/use-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useItem from "hooks/useItem";
import type { Document, StrapiFile } from "types";
import PageLoading from "common/components/PageLoading/PageLoading";
import Icon from "components/Icon/Icon";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import FileIcon from "common/components/FileUploader/FileIcon";
import { Constants } from "constants/Constants";
import { downloadFile } from "common/utils/downloadFile";
import { Gallery, Item } from "react-photoswipe-gallery";
import "photoswipe/dist/photoswipe.css";
import Button from "common/components/Button/Button";
import { ProductNameLabel } from "pages/Products/productUtils";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp"];

function isImage(ext?: string) {
  return IMAGE_EXTENSIONS.includes(ext?.toLowerCase() ?? "");
}

function ImagePreview({
  file,
  fileUrl,
}: {
  file: StrapiFile;
  fileUrl: string;
}) {
  const [width, setWidth] = useState(file.width > 0 ? file.width : 0);
  const [height, setHeight] = useState(file.height > 0 ? file.height : 0);

  useEffect(() => {
    if (width > 0 && height > 0) return undefined;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth > 0) {
        setWidth(img.naturalWidth);
        setHeight(img.naturalHeight);
      }
    };
    img.src = fileUrl;
    return () => {
      img.onload = null;
    };
  }, [fileUrl, width, height]);

  const displayName = file.caption || file.name;
  const thumbnailUrl = file.formats?.thumbnail
    ? ImageFromStrapiMedia(file.formats.thumbnail).uri
    : fileUrl;

  return (
    <Gallery withDownloadButton>
      <Item
        original={fileUrl}
        thumbnail={thumbnailUrl}
        width={width || 1200}
        height={height || 800}
        caption={displayName}
      >
        {({ ref, open }) => (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            <img
              ref={ref as React.MutableRefObject<HTMLImageElement>}
              src={thumbnailUrl}
              alt={displayName}
              onClick={open}
              className="w-12 h-12 object-cover rounded cursor-pointer shrink-0"
            />
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span
                className="text-sm font-semibold text-slate-700 truncate cursor-pointer"
                onClick={open}
              >
                {displayName}
              </span>
              {file.size && (
                <span className="text-xs text-slate-400">
                  {(file.size / 1000).toFixed(1)} Ko
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(fileUrl, file.name);
              }}
              className="p-2 rounded hover:bg-slate-200 transition-colors shrink-0"
              title="Télécharger"
              aria-label="Télécharger"
            >
              <Icon name="BiDownload" className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        )}
      </Item>
    </Gallery>
  );
}

function FilePreview({ file, fileUrl }: { file: StrapiFile; fileUrl: string }) {
  const displayName = file.caption || file.name;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
      <FileIcon ext={file.ext} className="w-10 h-10 shrink-0" />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="text-sm font-semibold text-slate-700 truncate">
          {displayName}
        </span>
        {file.size && (
          <span className="text-xs text-slate-400">
            {(file.size / 1000).toFixed(1)} Ko
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => downloadFile(fileUrl, file.name)}
        className="p-2 rounded hover:bg-slate-200 transition-colors shrink-0"
        title="Télécharger"
        aria-label="Télécharger"
      >
        <Icon name="BiDownload" className="w-4 h-4 text-slate-500" />
      </button>
    </div>
  );
}

function DocumentDetail() {
  const router = useRouter();
  const { t } = useTranslation();
  const id = router.query.id as string;

  const { item, isFetching } = useItem<Document>("documents", id);

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      router.navigate(-1);
      return;
    }
    router.navigate("/documents");
  }, [router]);

  if (isFetching && !item) return <PageLoading />;

  const doc = item as Document;
  const fileUrl = doc?.media?.url
    ? `${Constants.ROOT_URL}${doc.media.url}`
    : null;

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <div className="border rounded-lg border-slate-200 bg-white p-6 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t("actions.back")}
              className="flex justify-center items-center w-8 h-8 duration-200 ease-in-out relative right-0 hover:right-2"
            >
              <Icon name="ChevronLeftIcon" className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              {doc?.media?.ext && (
                <FileIcon ext={doc.media.ext} className="w-8 h-8 shrink-0" />
              )}
              <h1 className="text-2xl font-bold text-primary">
                {doc?.media?.caption || doc?.media?.name || "-"}
              </h1>
            </div>
          </div>
          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => router.navigate(`/documents/edit/${id}`)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {doc?.media && fileUrl && (
          <div className="white-box flex flex-col gap-3">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {t("documents.media")}
            </span>
            {isImage(doc.media.ext) ? (
              <ImagePreview file={doc.media} fileUrl={fileUrl} />
            ) : (
              <FilePreview file={doc.media} fileUrl={fileUrl} />
            )}
          </div>
        )}

        {doc?.products && doc.products.length > 0 && (
          <div className="white-box flex flex-col gap-3">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {t("documents.products")}
            </span>
            <div className="flex flex-wrap gap-3">
              {doc.products.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50"
                >
                  {product.image?.url ? (
                    <img
                      src={ImageFromStrapiMedia(product.image).uri}
                      alt={product.name}
                      className="w-8 h-8 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-slate-200 shrink-0" />
                  )}
                  <ProductNameLabel product={product} showColor={false} />
                </div>
              ))}
            </div>
          </div>
        )}

        {doc?.tags && doc.tags.length > 0 && (
          <div className="white-box flex flex-col gap-3">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {t("documents.tags")}
            </span>
            <div className="flex flex-wrap gap-2">
              {doc.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-3 py-1 text-sm rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {doc?.isSuggested != null && (
          <div className="white-box flex flex-col gap-3">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {t("documents.isSuggested")}
            </span>
            <span className="text-sm text-slate-700">
              {doc.isSuggested ? t("common.yes") : t("common.no")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentDetail;
